"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildDayView = buildDayView;
exports.buildBanners = buildBanners;
exports.todayFor = todayFor;
const schedule_js_1 = require("./schedule.js");
const time_js_1 = require("./time.js");
function buildDayView(input) {
    const { patient, medications, logicalDate, now } = input;
    const events = input.events.filter((e) => e.voidedAt == null);
    const nowMs = now.getTime();
    const scheduled = (0, schedule_js_1.buildDaySchedule)(medications, logicalDate, patient.tz);
    // Match logged doses to their slot. First write wins for a slot; any further dose of
    // the same medication is an extra, which is exactly the signal worth surfacing.
    const doseEvents = events.filter((e) => e.eventType === 'DOSE_TAKEN');
    const bySlot = new Map();
    for (const event of doseEvents) {
        if (event.medId === undefined || !event.slotId)
            continue;
        const slotKey = `${event.medId}|${event.slotId}`;
        const existing = bySlot.get(slotKey);
        if (!existing || event.takenAtUtc < existing.takenAtUtc)
            bySlot.set(slotKey, event);
    }
    const doses = scheduled.map((dose) => {
        const logged = bySlot.get(`${dose.medId}|${dose.slotId}`);
        const status = logged
            ? 'TAKEN'
            : nowMs > Date.parse(dose.windowEndUtc)
                ? 'MISSED'
                : nowMs >= Date.parse(dose.windowStartUtc)
                    ? 'DUE'
                    : 'UPCOMING';
        const card = {
            medId: dose.medId,
            medVersion: dose.medVersion,
            medName: dose.medName,
            medLabel: dose.medLabel,
            slotId: dose.slotId,
            slotLabel: dose.slotLabel,
            localTime: dose.localTime,
            quantity: dose.quantity,
            dueAtUtc: dose.dueAtUtc,
            windowStartUtc: dose.windowStartUtc,
            windowEndUtc: dose.windowEndUtc,
            status,
            confirmationStatus: dose.confirmationStatus,
        };
        if (logged) {
            card.takenAtUtc = logged.takenAtUtc;
            card.takenAtLocal = logged.localTime ?? (0, time_js_1.formatInZone)(logged.takenAtUtc, patient.tz, 'HH:mm');
            card.takenEventId = logged.eventId;
            if (logged.actorRole !== undefined)
                card.actorRole = logged.actorRole;
        }
        return card;
    });
    const prn = (0, schedule_js_1.asNeededMedications)(medications).map((med) => {
        const taken = doseEvents
            .filter((e) => e.medId === med.medId)
            .sort((a, b) => a.takenAtUtc.localeCompare(b.takenAtUtc));
        const card = {
            medId: med.medId,
            medVersion: med.version,
            medName: med.name,
            medLabel: (0, schedule_js_1.medLabelFor)(med),
            confirmationStatus: med.confirmationStatus,
            takenTodayCount: taken.length,
            advisoryMaxPerDay: med.advisoryMaxPerDay ?? null,
            advisoryMinIntervalHours: med.advisoryMinIntervalHours ?? null,
            advisorySource: med.advisorySource ?? null,
        };
        const last = taken.at(-1);
        if (last)
            card.lastTakenAtUtc = last.takenAtUtc;
        return card;
    });
    return {
        patientId: patient.patientId,
        patientName: patient.displayName,
        isTest: patient.isTest ?? false,
        logicalDate,
        tz: patient.tz,
        dayStartHour: patient.dayStartHour,
        serverNowUtc: now.toISOString(),
        doses,
        prn,
        banners: buildBanners(medications, patient),
        events: events
            .map((e) => toEventView(e, patient.tz))
            .sort((a, b) => a.takenAtUtc.localeCompare(b.takenAtUtc)),
    };
}
/**
 * Standing prompts to resolve what nobody actually knows.
 *
 * These are not decoration. An unconfirmed frequency that stays visible is safer than
 * a guessed one that looks authoritative, and the banner carries the pharmacy number
 * so resolving it is one tap rather than a search.
 */
function buildBanners(medications, patient) {
    const banners = [];
    for (const med of medications) {
        if (med.confirmationStatus === 'UNCONFIRMED') {
            const banner = {
                kind: 'UNCONFIRMED_FREQUENCY',
                medId: med.medId,
                medName: med.name,
                title: `${med.name} — how often is not confirmed`,
                body: med.confirmationNote ??
                    'The prescription label does not say how often to take this. Please confirm with the pharmacy.',
            };
            if (patient.pharmacy?.name !== undefined)
                banner.pharmacyName = patient.pharmacy.name;
            if (patient.pharmacy?.phone !== undefined)
                banner.pharmacyPhone = patient.pharmacy.phone;
            banners.push(banner);
        }
        else if (med.confirmationStatus === 'PROBABLE' && !(0, schedule_js_1.isAsNeeded)(med.scheduleType)) {
            banners.push({
                kind: 'PROBABLE_SCHEDULE',
                medId: med.medId,
                medName: med.name,
                title: `${med.name} — schedule read from a photo`,
                body: med.confirmationNote ??
                    'This schedule was read from a photograph of the label and has not been confirmed.',
            });
        }
    }
    return banners;
}
function toEventView(event, tz) {
    const view = {
        eventId: event.eventId,
        eventType: event.eventType,
        takenAtUtc: event.takenAtUtc,
        takenAtLocal: event.localTime ?? (0, time_js_1.formatInZone)(event.takenAtUtc, tz, 'HH:mm'),
        logicalDate: event.logicalDate,
        timeQuality: event.timeQuality ?? 'GOOD',
        actorRole: event.actorRole ?? 'SELF',
        summary: summarize(event),
    };
    if (event.actorName !== undefined)
        view.actorName = event.actorName;
    if (event.medId !== undefined)
        view.medId = event.medId;
    if (event.slotId != null)
        view.slotId = event.slotId;
    if (event.extraDose !== undefined)
        view.extraDose = event.extraDose;
    if (event.redFlag !== undefined)
        view.redFlag = event.redFlag;
    return view;
}
function summarize(event) {
    switch (event.eventType) {
        case 'DOSE_TAKEN':
            return event.medId ?? 'Dose taken';
        case 'SYMPTOM':
            return (event.symptoms ?? []).join(', ');
        case 'EXTRA_SUBSTANCE':
            return event.substanceName ?? event.substanceKey ?? 'Something else';
        case 'VITALS':
            return 'Vitals';
        default:
            return event.eventType;
    }
}
/** Today's chart row for a patient, in their own timezone. */
function todayFor(patient, now) {
    return (0, time_js_1.resolveLogicalDate)(now, patient.tz, patient.dayStartHour);
}
//# sourceMappingURL=dayview.js.map